import { useRef } from "react";
import { gsap, useGSAP } from "../gsap";
import { LOGO } from "../content";

// The cut runs from 62% down the left edge to 38% down the right edge.
const CUT_LEFT = 62;
const CUT_RIGHT = 38;
const cutY = (x: number) => CUT_LEFT + ((CUT_RIGHT - CUT_LEFT) * x) / 100;

// A sliver lying on the cut, tapered at both ends; `half` is its thickness at the middle.
const wound = (half: number) =>
  `0,${CUT_LEFT} 50,${cutY(50) - half} 100,${CUT_RIGHT} 50,${cutY(50) + half}`;

// Sizes are in vmin but never drop below a phone-friendly pixel size.
const fluid = (vmin: number) => `max(${vmin}vmin, ${vmin * 5}px)`;

const TOP_HALF = `polygon(0 0, 100% 0, 100% ${CUT_RIGHT}%, 0 ${CUT_LEFT}%)`;
const BOTTOM_HALF = `polygon(0 ${CUT_LEFT}%, 100% ${CUT_RIGHT}%, 100% 100%, 0 100%)`;

// Pools that well up on the cut itself: x in % of width, dy in % of height off the cut, size in vmin.
const POOLS = [
  { x: 8, dy: -0.5, size: 4 },
  { x: 17, dy: 1, size: 8 },
  { x: 25, dy: -1, size: 5 },
  { x: 31, dy: 1, size: 12 },
  { x: 39, dy: -1, size: 6 },
  { x: 44, dy: 1.5, size: 9 },
  { x: 50, dy: 1, size: 16 },
  { x: 56, dy: -1.5, size: 7 },
  { x: 61, dy: 2, size: 10 },
  { x: 69, dy: -1, size: 13 },
  { x: 76, dy: 1, size: 5 },
  { x: 81, dy: 1, size: 8 },
  { x: 93, dy: 0.5, size: 5 },
];

// Small seeded generator, so the spatter looks random but is the same on every load.
function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Spatter thrown off by the blade: many small drops near the impact, fewer and
// smaller further out, each stretched along the direction it travelled.
const SPATTER = (() => {
  const rand = mulberry32(7);
  const drops = Array.from({ length: 54 }, () => {
    const spread = (rand() + rand() + rand()) / 3 - 0.5; // bell curve around the impact
    const x = Math.min(97, Math.max(3, 50 + spread * 150));
    const side = rand() < 0.5 ? -1 : 1;
    const distance = Math.min(30, 2.5 - Math.log(1 - rand()) * 7); // vmin off the cut
    const size = Math.max(1.2, 4.6 * Math.exp(-distance / 10) * (0.5 + rand()));
    const stretch = 1 + Math.min(1.8, distance / 9) * (0.6 + rand() * 0.4);
    const dx = (x - 50) * 1.3;
    const dy = side * distance;
    // Rotated so the long axis of the drop points away from the impact.
    const rotate = (Math.atan2(dx, -dy) * 180) / Math.PI;
    return { x, dy, distance, size, stretch, rotate };
  });
  return drops.sort((a, b) => a.distance - b.distance);
})();

// Runs that gravity pulls out of the bigger pools: length in vh, width in vmin, time in seconds.
const DRIPS = [
  { x: 17, length: 15, width: 2.2, duration: 1.05, delay: 0.2 },
  { x: 31, length: 30, width: 3, duration: 1.3, delay: 0.05 },
  { x: 44, length: 11, width: 2, duration: 0.9, delay: 0.3 },
  { x: 50, length: 38, width: 3.6, duration: 1.4, delay: 0 },
  { x: 53.5, length: 17, width: 2, duration: 1.1, delay: 0.25 },
  { x: 61, length: 22, width: 2.6, duration: 1.15, delay: 0.12 },
  { x: 69, length: 31, width: 3.2, duration: 1.35, delay: 0.08 },
  { x: 81, length: 13, width: 2.2, duration: 0.95, delay: 0.28 },
];

function Half({ clip, className }: { clip: string; className: string }) {
  return (
    <div className={`absolute inset-0 flex items-center justify-center bg-ink ${className}`} style={{ clipPath: clip }}>
      <img src={LOGO} alt="" width={625} height={625} className="loader-logo h-[34vmin] w-[34vmin]" />
    </div>
  );
}

type Props = {
  /** Fires as the wound starts to open, so the page can begin its own entrance underneath. */
  onReveal: () => void;
  /** Fires when the loader has fully left the screen. */
  onDone: () => void;
};

export function Loader({ onReveal, onDone }: Props) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap
        .timeline({ onComplete: onDone })
        .from(".loader-logo", { opacity: 0, scale: 0.9, duration: 0.6, ease: "power2.out" })
        // The stab: one fast stroke across the screen, and the screen takes the hit.
        .fromTo(
          ".loader-cut",
          { clipPath: "inset(0% 100% 0% 0%)" },
          { clipPath: "inset(0% 0% 0% 0%)", duration: 0.18, ease: "power4.in" },
          "+=0.25",
        )
        .to(root.current, { keyframes: { x: [0, -14, 10, -5, 0], y: [0, 8, -6, 3, 0] }, duration: 0.3, ease: "none" })
        .addLabel("hit", "<")
        .set(".loader-stains", { opacity: 1 }, "hit")
        // The wound bleeds: spatter flies off the blade, pools well up on the cut.
        .from(
          ".loader-spatter",
          {
            y: (_, el: HTMLElement) => el.offsetHeight * 2.5,
            scale: 0.2,
            duration: 0.3,
            ease: "power3.out",
            stagger: { amount: 0.14 },
          },
          "hit",
        )
        .from(
          ".loader-pool",
          { scale: 0, duration: 0.8, ease: "power2.out", stagger: { each: 0.03, from: "center" } },
          "hit",
        )
        .to(".loader-wound", { attr: { points: wound(1.4) }, duration: 0.5, ease: "power2.out" }, "hit")
        // Gravity takes over: each run creeps down at its own pace behind a heavy bead.
        .from(
          ".loader-drip-run",
          {
            scaleY: 0,
            ease: "power1.inOut",
            duration: (i) => DRIPS[i]!.duration,
            delay: (i) => DRIPS[i]!.delay,
          },
          "hit+=0.2",
        )
        .from(
          ".loader-drip-bead",
          {
            y: (_, el: HTMLElement) => -el.parentElement!.offsetHeight,
            ease: "power1.inOut",
            duration: (i) => DRIPS[i]!.duration,
            delay: (i) => DRIPS[i]!.delay,
          },
          "hit+=0.2",
        )
        // The wound opens and the page is underneath.
        .addLabel("open", "hit+=1.5")
        .call(onReveal, undefined, "open")
        .to(".loader-top", { yPercent: -110, xPercent: -5, duration: 1, ease: "expo.inOut" }, "open")
        .to(".loader-bottom", { yPercent: 110, xPercent: 5, duration: 1, ease: "expo.inOut" }, "open")
        .to(".loader-wound", { opacity: 0, duration: 0.25, ease: "power2.out" }, "open")
        .to(".loader-stains", { opacity: 0, scale: 1.25, duration: 0.6, ease: "power2.in" }, "open+=0.1");
    },
    { scope: root },
  );

  return (
    <div ref={root} aria-hidden className="pointer-events-auto fixed inset-0 z-[55] overflow-hidden">
      <Half clip={TOP_HALF} className="loader-top" />
      <Half clip={BOTTOM_HALF} className="loader-bottom" />

      <svg className="absolute h-0 w-0">
        <filter id="loader-goo" x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
          {/* 1. Blur and threshold, so neighbouring shapes melt into one liquid. */}
          <feGaussianBlur in="SourceGraphic" stdDeviation="4.5" result="blur" />
          <feColorMatrix in="blur" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -7" result="goo" />
          {/* 2. Push the edges around with noise, so nothing stays a clean circle. */}
          <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="3" seed="4" result="noise" />
          <feDisplacementMap in="goo" in2="noise" scale="20" xChannelSelector="R" yChannelSelector="G" result="rough" />
          {/* 3. A soft highlight from the upper left, so the liquid reads as wet and thick. */}
          <feGaussianBlur in="rough" stdDeviation="3.5" result="bump" />
          <feSpecularLighting
            in="bump"
            surfaceScale="5"
            specularConstant="0.8"
            specularExponent="28"
            lightingColor="#e6ccff"
            result="shine"
          >
            <feDistantLight azimuth="235" elevation="52" />
          </feSpecularLighting>
          <feComposite in="shine" in2="rough" operator="in" result="wet" />
          <feComposite in="rough" in2="wet" operator="arithmetic" k1="0" k2="1" k3="0.5" k4="0" />
        </filter>
      </svg>

      <div className="loader-stains absolute inset-0 opacity-0" style={{ filter: "url(#loader-goo)" }}>
        {POOLS.map((s, i) => (
          <span
            key={i}
            className="loader-pool absolute rounded-full bg-stab"
            style={{
              left: `${s.x}%`,
              top: `${cutY(s.x) + s.dy}%`,
              width: fluid(s.size),
              height: fluid(s.size * 0.8),
              translate: "-50% -50%",
              rotate: "-13deg",
            }}
          />
        ))}
        {SPATTER.map((s, i) => (
          <span
            key={i}
            className="loader-spatter absolute rounded-full bg-stab"
            style={{
              left: `${s.x}%`,
              top: `calc(${cutY(s.x)}% + ${s.dy}vmin)`,
              width: fluid(s.size),
              height: fluid(s.size * s.stretch),
              translate: "-50% -50%",
              rotate: `${s.rotate}deg`,
            }}
          />
        ))}
        {DRIPS.map((d, i) => (
          <span
            key={i}
            className="absolute"
            style={{
              left: `${d.x}%`,
              top: `${cutY(d.x)}%`,
              width: fluid(d.width),
              height: `${d.length}vh`,
              translate: "-50% 0",
            }}
          >
            <span className="loader-drip-run absolute inset-x-[24%] inset-y-0 origin-top bg-stab" />
            <span className="loader-drip-bead absolute bottom-0 left-0 aspect-[4/5] w-full translate-y-1/2 rounded-full bg-stab" />
          </span>
        ))}
      </div>

      {/* The blade stroke: a sliver tapered at both ends, lying on the cut. */}
      <svg
        className="loader-cut absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ clipPath: "inset(0% 100% 0% 0%)" }}
      >
        <polygon
          className="loader-wound fill-bone"
          points={wound(0.35)}
        />
      </svg>
    </div>
  );
}
